import { getHeaders } from "$utils/utils";
import { getToken } from "$utils/token";
import { JobRecord, JobType } from "$types/Job";
import config from "./config";

const url = `${config.api_url}/api/jobs`;

const authHeaders = (token?: string) => getHeaders(token ?? getToken());

const parseError = async (response: Response, fallback: string) => {
  try {
    const body = await response.json();
    return new Error(body?.message || fallback);
  } catch {
    return new Error(fallback);
  }
};

export interface CreateJobInput {
  type: JobType;
  params?: unknown;
  /** Overrides the default label shown in the toast. */
  label?: string;
}

/** Returns immediately with a pending job; the work happens in the worker. */
export const createJob = async (
  input: CreateJobInput,
  token?: string
): Promise<JobRecord> => {
  const response = await fetch(url, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(input),
    cache: "no-store",
  });

  if (!response.ok) {
    throw await parseError(response, "Impossible de lancer le traitement.");
  }
  return response.json();
};

export const getJob = async (
  jobId: string,
  token?: string
): Promise<JobRecord> => {
  const response = await fetch(`${url}/${jobId}`, {
    headers: authHeaders(token),
    cache: "no-store",
  });

  if (!response.ok) {
    throw await parseError(response, "Tâche introuvable.");
  }
  return response.json();
};

export const listJobs = async (
  options: { activeOnly?: boolean } = {},
  token?: string
): Promise<JobRecord[]> => {
  const response = await fetch(
    options.activeOnly ? `${url}?active=true` : url,
    { headers: authHeaders(token), cache: "no-store" }
  );

  if (!response.ok) {
    throw await parseError(response, "Impossible de récupérer les tâches.");
  }
  return response.json();
};

/**
 * Fetched as a blob rather than navigated to, so the Authorization header is sent
 * and no popup blocker is involved.
 */
export const downloadJobFile = async (
  jobId: string,
  token?: string
): Promise<Blob> => {
  const response = await fetch(`${url}/${jobId}/file`, {
    headers: authHeaders(token),
    cache: "no-store",
  });

  if (!response.ok) {
    throw await parseError(response, "Le fichier n'est pas disponible.");
  }
  return response.blob();
};

export const cancelJob = async (
  jobId: string,
  token?: string
): Promise<JobRecord> => {
  const response = await fetch(`${url}/${jobId}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseError(response, "Impossible d'annuler la tâche.");
  }
  return response.json();
};
